import { useCallback, useMemo, useReducer } from "react";
import { eventoVacio, fechaVacia } from "../domain/evento/schema";
import { validarEvento, hayErrores } from "../domain/evento/validacion";

/* Estado del formulario de evento: un solo reducer para crear y editar.
 *
 * Cada campo se toca con `setCampo`, y el error de ese campo se limpia solo al
 * modificarlo. Antes había un handler + un useCallback por sección en cada
 * vista, con comparaciones por JSON.stringify para evitar loops de efectos. */

const ERRORES_POR_CAMPO = {
  evento: ["evento"],
  descripcion: ["descripcion"],
  fechas: ["fecha"],
  ciudad: ["direccion"],
  direccion: ["direccion"],
  provinciaId: ["direccion"],
  eventos_tipos_id: ["eventos_tipos_id"],
  privado: ["publicoprivado"],
  media: ["files"],
  sponsors: ["sponsors"],
};

const limpiarErrores = (errores, campo) => {
  const aLimpiar = ERRORES_POR_CAMPO[campo];
  if (!aLimpiar?.length) return errores;

  const siguientes = { ...errores };
  aLimpiar.forEach((clave) => delete siguientes[clave]);
  return siguientes;
};

const reducer = (estado, accion) => {
  switch (accion.tipo) {
    case "setCampo": {
      const { campo, valor } = accion;
      return {
        ...estado,
        form: { ...estado.form, [campo]: valor },
        errores: limpiarErrores(estado.errores, campo),
      };
    }

    case "agregarFecha":
      return {
        ...estado,
        form: { ...estado.form, fechas: [...estado.form.fechas, fechaVacia()] },
      };

    case "eliminarFecha": {
      if (estado.form.fechas.length <= 1) return estado;
      const fechas = estado.form.fechas.filter((_, i) => i !== accion.indice);
      return {
        ...estado,
        form: { ...estado.form, fechas },
        errores: limpiarErrores(estado.errores, "fechas"),
      };
    }

    case "setFecha": {
      const fechas = estado.form.fechas.map((fecha, i) =>
        i === accion.indice ? { ...fecha, [accion.campo]: accion.valor } : fecha
      );
      return {
        ...estado,
        form: { ...estado.form, fechas },
        errores: limpiarErrores(estado.errores, "fechas"),
      };
    }

    case "setErrores":
      return { ...estado, errores: accion.errores };

    case "reiniciar":
      return { form: accion.form, errores: {} };

    default:
      return estado;
  }
};

const useEventoForm = (valoresIniciales) => {
  const [estado, dispatch] = useReducer(reducer, undefined, () => ({
    form: valoresIniciales ?? eventoVacio(),
    errores: {},
  }));

  const setCampo = useCallback((campo, valor) => {
    dispatch({ tipo: "setCampo", campo, valor });
  }, []);

  const setFecha = useCallback((indice, campo, valor) => {
    dispatch({ tipo: "setFecha", indice, campo, valor });
  }, []);

  const agregarFecha = useCallback(() => dispatch({ tipo: "agregarFecha" }), []);

  const eliminarFecha = useCallback(
    (indice) => dispatch({ tipo: "eliminarFecha", indice }),
    []
  );

  const reiniciar = useCallback(
    (form) => dispatch({ tipo: "reiniciar", form: form ?? eventoVacio() }),
    []
  );

  /* Valida y devuelve si puede enviarse. Los errores se guardan en el estado
   * para que cada sección muestre el suyo. */
  const validar = useCallback(() => {
    const errores = validarEvento(estado.form);
    dispatch({ tipo: "setErrores", errores });
    return !hayErrores(errores);
  }, [estado.form]);

  const acciones = useMemo(
    () => ({ setCampo, setFecha, agregarFecha, eliminarFecha, reiniciar, validar }),
    [setCampo, setFecha, agregarFecha, eliminarFecha, reiniciar, validar]
  );

  return {
    form: estado.form,
    errores: estado.errores,
    ...acciones,
  };
};

export default useEventoForm;
